import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Tag,
  Select,
  Button,
  Space,
  Drawer,
  Typography,
  Descriptions,
  Image,
  Input,
  Form,
  message,
  Avatar,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import request from '@/utils/request';
import {
  FEEDBACK_CONTENT_MAX_LEN,
  type FeedbackListItemForAdmin,
  type FeedbackStatus,
  type FeedbackSummary,
  type PaginatedData,
} from '@zero/shared';

const { Title, Text, Paragraph } = Typography;

const statusTagColor: Record<FeedbackStatus, string> = {
  pending: 'orange',
  replied: 'green',
};

const statusLabel: Record<FeedbackStatus, string> = {
  pending: '待回复',
  replied: '已回复',
};

type StatusFilter = 'all' | FeedbackStatus;

const FeedbackPage: React.FC = () => {
  const [items, setItems] = useState<FeedbackListItemForAdmin[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [detail, setDetail] = useState<FeedbackListItemForAdmin | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyForm] = Form.useForm<{ adminReply: string }>();
  const [replying, setReplying] = useState(false);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const { data } = await request.get<FeedbackSummary>('/admin/feedback/summary');
      setSummary(data);
    } catch (err) {
      message.error((err as Error)?.message || '加载统计失败');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await request.get<PaginatedData<FeedbackListItemForAdmin>>(
        '/admin/feedback',
        { params: { status, page, pageSize } },
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      message.error((err as Error)?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openDetail = useCallback(
    async (id: number) => {
      setDetailOpen(true);
      setDetailLoading(true);
      replyForm.resetFields();
      try {
        const { data } = await request.get<FeedbackListItemForAdmin>(
          `/admin/feedback/${id}`,
        );
        setDetail(data);
      } catch (err) {
        message.error((err as Error)?.message || '加载详情失败');
        setDetailOpen(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [replyForm],
  );

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    replyForm.resetFields();
  };

  const handleReply = async (values: { adminReply: string }) => {
    if (!detail) return;
    setReplying(true);
    try {
      const { data } = await request.post<FeedbackListItemForAdmin>(
        `/admin/feedback/${detail.id}/reply`,
        { adminReply: values.adminReply },
      );
      message.success('回复已提交');
      setDetail((prev) => (prev ? { ...prev, ...data } : prev));
      loadList();
      loadSummary();
    } catch (err) {
      message.error((err as Error)?.message || '回复失败');
    } finally {
      setReplying(false);
    }
  };

  const columns: ColumnsType<FeedbackListItemForAdmin> = [
    {
      title: '用户',
      key: 'user',
      width: 200,
      render: (_, row) => (
        <Space>
          <Avatar
            size={32}
            src={row.user.avatarAccessUrl || undefined}
            icon={<UserOutlined />}
          />
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 500 }}>
              {row.user.nickname || '未命名'}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.user.openId.slice(0, 8)}…
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 480 }}>
          {text}
        </Text>
      ),
    },
    {
      title: '图片',
      key: 'images',
      width: 80,
      render: (_, row) => row.images.length,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: FeedbackStatus) => (
        <Tag color={statusTagColor[s]}>{statusLabel[s]}</Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (iso: string) => dayjs(iso).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, row) => (
        <Button type="link" onClick={() => openDetail(row.id)}>
          {row.status === 'pending' ? '查看 / 回复' : '查看'}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 16 }}>
        意见反馈
      </Title>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card loading={summaryLoading}>
            <Statistic title="总数" value={summary?.total ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card loading={summaryLoading}>
            <Statistic
              title="待回复"
              value={summary?.pending ?? 0}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card loading={summaryLoading}>
            <Statistic
              title="已回复"
              value={summary?.replied ?? 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <span>反馈列表</span>
            <Select
              size="small"
              style={{ width: 120 }}
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={[
                { label: '全部', value: 'all' },
                { label: '待回复', value: 'pending' },
                { label: '已回复', value: 'replied' },
              ]}
            />
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadList();
              loadSummary();
            }}
          >
            刷新
          </Button>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <Drawer
        title={`反馈 #${detail?.id ?? ''}`}
        open={detailOpen}
        onClose={closeDetail}
        width={640}
        destroyOnClose
      >
        {detailLoading || !detail ? (
          <div style={{ textAlign: 'center', padding: 48 }}>加载中…</div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="用户">
                <Space>
                  <Avatar
                    size={24}
                    src={detail.user.avatarAccessUrl || undefined}
                    icon={<UserOutlined />}
                  />
                  <span>{detail.user.nickname || '未命名'}</span>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {detail.user.openId}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusTagColor[detail.status]}>
                  {statusLabel[detail.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="提交时间">
                {dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Text strong>反馈内容</Text>
              <Paragraph
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: '#fafafa',
                  borderRadius: 6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {detail.content}
              </Paragraph>
            </div>

            {detail.images.length > 0 && (
              <div>
                <Text strong>图片（{detail.images.length}）</Text>
                <div style={{ marginTop: 8 }}>
                  <Image.PreviewGroup>
                    <Space wrap>
                      {detail.images.map((img) => (
                        <Image
                          key={img.id}
                          src={img.accessUrl}
                          width={120}
                          height={120}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                        />
                      ))}
                    </Space>
                  </Image.PreviewGroup>
                </div>
              </div>
            )}

            {detail.status === 'replied' ? (
              <Card size="small" title="管理员回复（已发送）">
                <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                  {detail.adminReply}
                </Paragraph>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {detail.repliedByUsername || `管理员 #${detail.repliedBy}`} ·{' '}
                  {detail.repliedAt
                    ? dayjs(detail.repliedAt).format('YYYY-MM-DD HH:mm:ss')
                    : ''}
                </Text>
              </Card>
            ) : (
              <Card size="small" title="回复（提交后不可修改）">
                <Form
                  form={replyForm}
                  layout="vertical"
                  onFinish={handleReply}
                >
                  <Form.Item
                    name="adminReply"
                    rules={[
                      { required: true, message: '请输入回复内容' },
                      {
                        max: FEEDBACK_CONTENT_MAX_LEN,
                        message: `最多 ${FEEDBACK_CONTENT_MAX_LEN} 字`,
                      },
                    ]}
                  >
                    <Input.TextArea
                      rows={5}
                      maxLength={FEEDBACK_CONTENT_MAX_LEN}
                      showCount
                      placeholder="请输入回复内容"
                    />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={replying}
                      >
                        发送回复
                      </Button>
                      <Button onClick={closeDetail}>关闭</Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Card>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default FeedbackPage;
